import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { BehaviorSubject, map, Observable, startWith, Subscription } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { LanguageService } from '@fcom/ui-translate';
import { ButtonMode, IconButtonSize, IconButtonTheme, NotificationTheme } from '@fcom/ui-components';
import { pathIsWithinHardcodedPath, unsubscribe } from '@fcom/core/utils';
import { CountryState, NativeBridgeService, RootPaths } from '@fcom/core';
import { finShare } from '@fcom/rx';

import { SelectOption } from '../../interfaces';
import { getCountryStates } from '../../utils/language.utils';

export interface CountryAndLanguageSelection {
  country: string;
  languageCode: string;
}

interface CountryAndLanguageSelectionForm {
  country: FormControl<string>;
  language: FormControl<string>;
}

@Component({
  selector: 'fin-country-and-language-selector',
  templateUrl: './country-and-language-selector.component.html',
  styleUrls: ['country-and-language-selector.component.scss'],
  standalone: false,
})
export class CountryAndLanguageSelectorComponent implements OnInit, OnDestroy {
  private fb = inject(UntypedFormBuilder);
  private languageService = inject(LanguageService);
  private router = inject(Router);
  private nativeBridge = inject(NativeBridgeService);

  readonly ButtonMode = ButtonMode;
  readonly IconButtonSize = IconButtonSize;
  readonly IconButtonTheme = IconButtonTheme;
  readonly NotificationTheme = NotificationTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  @Input()
  useH1 = false;

  @Input()
  prefill = true;

  @Input()
  hideLanguageOptions = false;

  @Input()
  lockLanguageSelection = false;

  @Input()
  hideTitle = false;

  @Input()
  showCloseButton = true;

  @Output()
  applySelection = new EventEmitter<CountryAndLanguageSelection>();

  @Output()
  closeClick = new EventEmitter<void>();

  subscriptions: Subscription = new Subscription();
  countries: CountryState[] = getCountryStates();

  countryOptions: SelectOption[];
  formGroup: FormGroup<CountryAndLanguageSelectionForm>;
  showFlag = false;

  languageOptions$: Observable<SelectOption[]>;
  countryShopInfo$ = new BehaviorSubject<string>('');
  countryShopWarning$ = new BehaviorSubject<string>('');

  ngOnInit(): void {
    this.countryOptions = this.countries.map((country) => {
      const { name, code, currencyCode, currencySymbol } = country;
      const optionName = currencyCode ? `${name} (${currencyCode} ${currencySymbol})` : name;
      return { name: optionName, value: code };
    });

    const langValue = this.languageService.langValue === 'master-en' ? 'gb-en' : this.languageService.langValue;

    const country = langValue?.includes('-') ? langValue.split('-')?.[0] : 'int';

    this.formGroup = this.fb.group({
      country: this.fb.control(this.prefill ? country.toUpperCase() : '', Validators.required),
      language: this.fb.control('', Validators.required),
    });

    this.languageOptions$ = this.formGroup.controls.country.valueChanges.pipe(
      startWith(this.prefill ? country.toUpperCase() : ''),
      map(
        (countryCode) =>
          this.countries
            .find((country) => country.code === countryCode)
            ?.languages.map((countryLanguage) => ({ name: countryLanguage.name, value: countryLanguage.code })) || []
      ),
      finShare()
    );

    this.formGroup.valueChanges.subscribe((newSelection) => {
      const countryCode = newSelection.country;
      this.countryShopInfo$.next('');
      this.countryShopWarning$.next('');
      const selectedCountry = this.countries.find((country) => country.code === countryCode);
      const countryChanged = countryCode !== country.toUpperCase();
      const languageChanged = newSelection.language && newSelection.language !== langValue;
      const countryOrLanguageChanged = countryChanged || languageChanged;
      const isInBooking = pathIsWithinHardcodedPath(this.router.url, RootPaths.BOOKING_ROOT);
      if (selectedCountry && !selectedCountry.currencyCode) {
        this.countryShopInfo$.next('Prices on this site are in the currency of the country of departure.');
      }
      if (countryOrLanguageChanged && isInBooking && !this.nativeBridge.isInsideNativeWebView) {
        this.countryShopWarning$.next(
          'Changing these preferences will reload this page, and you will lose any input information.'
        );
      }
    });

    this.subscriptions.add(
      this.languageOptions$.subscribe((options) => {
        if (!options || options.length === 0) {
          this.formGroup.controls.language.disable();
        } else {
          this.formGroup.controls.language.enable();
          const matchingValue = options.find((option) => option.value === langValue);

          if (matchingValue) {
            this.formGroup.controls.language.setValue(matchingValue.value);
          } else if (options.length === 1) {
            this.formGroup.controls.language.setValue(options[0].value);
          }

          if (this.lockLanguageSelection) {
            const finnishOption = options.find((option) => option.value.endsWith('-fi'));
            const englishOption = options.find((option) => option.value === 'en' || option.value.endsWith('-en'));
            this.formGroup.controls.language.setValue(finnishOption?.value ?? englishOption.value);
          }

          if (options.length === 1 || this.lockLanguageSelection) {
            this.formGroup.controls.language.disable();
          }

          this.showFlag = true;
        }
      })
    );
  }

  apply(): void {
    const countryCode = this.formGroup.controls.country.value;
    const selectedCountry = this.countries.find((country) => country.code === countryCode);
    const languageCode = this.getSelectedLanguage(selectedCountry);
    this.applySelection.emit({ country: countryCode, languageCode });
  }

  updateShowFlag(dropDownOpen: boolean): void {
    this.showFlag = !dropDownOpen && Boolean(this.formGroup.controls.country.value);
  }

  emitClose(): void {
    this.closeClick.emit();
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  private getSelectedLanguage(country: CountryState): string {
    if (!this.hideLanguageOptions) {
      return this.formGroup.controls.language.value;
    }
    const sameLanguageInSelectedCountry = country.languages.find(
      (countryLanguage) => countryLanguage.ISOCode === this.languageService.langKeyValue
    );
    return sameLanguageInSelectedCountry?.code ?? country.languages[0].code;
  }
}
